import React, { useState } from 'react';
import ButtonClickTable from '../hooks/ButtonClickTable';
import { utils, writeFile } from 'xlsx';
import RowList from '../components/ButtonClickRow';

const AnalyticButtonClick = () => {
    const { data, loading, error: ButtonListError, reloadData, isDataFullyLoaded} = ButtonClickTable();
    const [stateFilter, setStateFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('');
    const [buttonTextFilter, setButtonTextFilter] = useState('');
    const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const resetFilters = () => {
        setStateFilter('all');
        setUserFilter('');
        setButtonTextFilter('');
        setDateFilter({ start: '', end: '' });
    };

    const filterDataByTime = (data, filter) => {
        return data.filter(item => {
            const dayStatus = item.dayStatus;
            switch (filter) {
                case 'morning': return dayStatus === 'Утро';
                case 'afternoon': return dayStatus === 'День';
                case 'evening': return dayStatus === 'Вечер';
                default: return true;
            }
        });
    };

    const filterDataByUser = (data, filter) => {
        return data.filter(item => 
            item.userId.toString().includes(filter) || 
            (item.User?.userId?.toString().includes(filter))
        );
    };

    const filterDataByButtonText = (data, filter) => {
        return data.filter(item => item.buttonText.toLowerCase().includes(filter.toLowerCase()));
    };

    const filterDataByDate = (data, filter) => {
        return data.filter(item => {
            const itemDate = new Date(item.timestamp);
            const startDate = filter.start ? new Date(filter.start) : null;
            const endDate = filter.end ? new Date(filter.end) : null;
            return (!startDate || itemDate >= startDate) && (!endDate || itemDate <= endDate);
        });
    };

    const filteredData = filterDataByDate(
        filterDataByButtonText(
            filterDataByUser(
                filterDataByTime(data, stateFilter),
                userFilter
            ),
            buttonTextFilter
        ),
        dateFilter
    );

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortColumn) return 0;

        let aValue, bValue;

        if (sortColumn === 'firstName') {
            aValue = a.User?.firstName || ''; // Добавлена проверка
            bValue = b.User?.firstName || ''; // Добавлена проверка
        } else {
            aValue = a[sortColumn] || '';
            bValue = b[sortColumn] || '';
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const exportToExcel = () => {
        const filteredData = sortedData.map(list => ({
            'User ID': list.userId || '',
            'Telegram User ID': list.User?.userId || '', // Добавлена проверка
            'Имя пользователя': list.User?.firstName || 'Неизвестно', // Добавлена проверка
            'Текст кнопки': list.buttonText || '',
            'Время суток': list.dayStatus || '',
            'Время нажатия': list.timestamp || '',
        }));

        const worksheet = utils.json_to_sheet(filteredData);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Аналитика кнопок');
        writeFile(workbook, 'Аналитика нажатых кнопок.xlsx');
    };

    if (loading && !isDataFullyLoaded) {
        return (
            <div className="loading-container">
                <div className="loading-content">
                    <h3>Загрузка данных...</h3>
                    {data.length > 0 && (
                        <>
                            <p>Загружено {data.length} записей</p>
                            <progress value={data.length} max="10000" />
                        </>
                    )}
                    {!isDataFullyLoaded && data.length === 0 && (
                        <p>Идёт первоначальная загрузка данных...</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="analytics-container">
            <h2>Аналитика нажатых кнопок</h2>

            <div className="controls">
                <div className="filter-group">
                    <label htmlFor="time-filter">
                        Время суток:
                        <select
                            id="time-filter"
                            value={stateFilter}
                            onChange={(e) => setStateFilter(e.target.value)}
                        >
                            <option value="all">Все</option>
                            <option value="morning">Утро</option>
                            <option value="afternoon">День</option>
                            <option value="evening">Вечер</option>
                        </select>
                    </label>

                    <label htmlFor="user-filter">
                        ID пользователя:
                        <input
                            id="user-filter"
                            type="text"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            placeholder="Введите ID"
                        />
                    </label>

                    <label htmlFor="button-text-filter">
                        Текст кнопки:
                        <input
                            id="button-text-filter"
                            type="text"
                            value={buttonTextFilter}
                            onChange={(e) => setButtonTextFilter(e.target.value)}
                            placeholder="Введите текст"
                        />
                    </label>

                    <label htmlFor="date-filter-start">
                        Дата от:
                        <input
                            id="date-filter-start"
                            type="date"
                            value={dateFilter.start}
                            onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
                        />
                    </label>

                    <label htmlFor="date-filter-end">
                        Дата до:
                        <input
                            id="date-filter-end"
                            type="date"
                            value={dateFilter.end}
                            onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
                        />
                    </label>
                </div>

                <div className="action-buttons">
                    <button onClick={resetFilters} className="reset-btn">
                        Сбросить фильтры
                    </button>
                    <button 
                        onClick={exportToExcel} 
                        className="export-btn"
                        disabled={!sortedData || sortedData.length === 0}
                    >
                        Экспорт в Excel
                    </button>
                    <button onClick={reloadData} className="refresh-btn">
                        <i className='bx bx-refresh'></i>
                    </button>
                </div>
            </div>

            {sortedData.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('courseNumber')}>
                                    № {sortColumn === 'courseNumber' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('userId')}>
                                    User ID {sortColumn === 'userId' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('firstName')}>
                                    Имя {sortColumn === 'firstName' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('buttonText')}>
                                    Текст кнопки {sortColumn === 'buttonText' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('dayStatus')}>
                                    Время суток {sortColumn === 'dayStatus' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => handleSort('timestamp')}>
                                    Время нажатия {sortColumn === 'timestamp' && (
                                        sortDirection === 'asc' ? '↑' : '↓'
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((row, index) => (
                                <RowList
                                    key={index}
                                    userId={row.userId}
                                    telegramUserId={row.User?.userId || 'N/A'} // Добавлена проверка
                                    userName={row.User?.firstName || 'Неизвестно'} // Добавлена проверка
                                    buttonText={row.buttonText}
                                    dayStatus={row.dayStatus}
                                    timestamp={new Date(row.timestamp).toLocaleString()}
                                    courseNumber={index + 1}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>User ID</th>
                                <th>Имя</th>
                                <th>Текст кнопки</th>
                                <th>Время суток</th>
                                <th>Время нажатия</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td colSpan="6" className="no-data">
                                    Нет данных для отображения
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <style>{`
                .analytics-container {
                    padding: 20px;
                    font-family: Arial, sans-serif;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                h2 {
                    margin-bottom: 20px;
                    color: #333;
                }
                
                .controls {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 15px;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                }
                
                .filter-group {
                    display: flex;
                    gap: 15px;
                    flex-wrap: wrap;
                    align-items: flex-end;
                }
                
                .filter-group label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: 14px;
                }
                
                .filter-group input, 
                .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 14px;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 10px;
                    align-items: flex-end;
                }
                
                button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 14px;
                }
                
                button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .reset-btn {
                    background-color: #6c757d;
                    color: white;
                }
                
                .export-btn {
                    background-color: #28a745;
                    color: white;
                }
                
                .refresh-btn {
                    background-color: #17a2b8;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 8px;
                }
                
                button:hover:not(:disabled) {
                    opacity: 0.9;
                    transform: translateY(-1px);
                }
                
                .table-container {
                    overflow-x: auto;
                    border-radius: 8px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.05);
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                    background: white;
                }
                
                th, td {
                    padding: 12px 15px;
                    text-align: left;
                    border-bottom: 1px solid #dee2e6;
                }
                
                th {
                    background-color: #f8f9fa;
                    cursor: pointer;
                    position: relative;
                    font-weight: 600;
                    color: #495057;
                }
                
                th:hover {
                    background-color: #e9ecef;
                }
                
                tr:hover {
                    background-color: #f8f9fa;
                }
                
                .no-data {
                    text-align: center;
                    color: #6c757d;
                    padding: 20px;
                }
                
                .loading {
                    padding: 20px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin: 20px 0;
                }
                
                @media (max-width: 768px) {
                    .controls {
                        flex-direction: column;
                    }
                    
                    .filter-group {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    
                    .action-buttons {
                        justify-content: flex-start;
                    }
                    
                    th, td {
                        padding: 8px 10px;
                        font-size: 14px;
                    }
                }
            `}</style>
        </div>
    );
};


export default AnalyticButtonClick;